import { Title } from '@components/atoms/Title';
import { Dialog } from '@components/molecules/Dialog/Dialog';
import LoginFlow, { type LoginStep } from '@components/organisms/Authentication/LoginFlow/LoginFlow';
import { useMutation } from '@lib/graphql/urql';
import {
  type GetTvmediaNewsletterOverviewQuery,
  SubscribeToNewslettersCustomerioDocument,
} from '@src/lib/graphql/generated';
import { publicNewsletterApiContext } from '@src/lib/graphql/newsletterApi';
import Link from 'next/link';
import { getSession, useSession } from 'next-auth/react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { NewsletterConsent } from './NewsletterConsent';
import { NewsletterTopicCard } from './NewsletterTopicCard';
import type { NewsletterConsentContent } from './newsletterContent';

type Overview = GetTvmediaNewsletterOverviewQuery['newsletter_overviews'][number];
export interface NewsletterOverviewProps {
  overview?: Overview;
  consentContent?: NewsletterConsentContent;
}
const DRAFT_KEY = 'tvmedia-newsletter-selection';
const DEFAULT_TITLE = 'Alle TV-MEDIA Newsletter';
const DEFAULT_SUBHEADLINE =
  '<p>Deine Interessen sind vielfältig, unser Newsletter-Angebot ist es auch. Entdecke unsere redaktionell kuratierten Newsletter und wähle die Themen, zu denen du regelmäßig informiert und inspiriert werden möchtest.</p><p>Einfach einen Account im Zuge deiner Anmeldung erstellen und direkt in deinem Konto alle Newsletter verwalten. Ob An- oder Abmeldung, alles ein Kinderspiel, mit deinem persönlichen Zugang.</p>';

export const NewsletterOverview = ({ overview, consentContent }: NewsletterOverviewProps) => {
  const newsletters = useMemo(() => overview?.newsletters?.filter(Boolean) ?? [], [overview]);
  const { data: session, status } = useSession();
  const [selected, setSelected] = useState<string[]>([]);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState<string>(null);
  const [success, setSuccess] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [registeredProfile, setRegisteredProfile] = useState<{ email: string; firstName: string; lastName: string }>(
    null,
  );
  const [loginOpen, setLoginOpen] = useState(false);
  const [step, setStep] = useState<LoginStep>('start');
  const [{ fetching }, subscribeNewsletters] = useMutation(SubscribeToNewslettersCustomerioDocument);
  const submitting = useRef(false);
  useEffect(() => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (draft && Date.now() - draft.createdAt < 24 * 60 * 60 * 1000 && Array.isArray(draft.selected)) {
        setSelected(draft.selected.filter((id) => newsletters.some((newsletter) => newsletter.id === id)));
        setMarketing(draft.marketing === true);
      }
    } catch {
      /* An unavailable browser store must not prevent signup. */
    }
  }, [newsletters]);

  const subscribe = async (profile: { email?: string; firstName?: string; lastName?: string }, pending = false) => {
    if (submitting.current) return;
    if (!profile.email || !profile.firstName?.trim() || !profile.lastName?.trim()) {
      setError('Bitte vervollständige deinen Vor- und Nachnamen in den Konto-Einstellungen.');
      return;
    }
    submitting.current = true;
    setError(null);
    try {
      const result = await subscribeNewsletters(
        {
          data: selected.map((newsletter) => ({
            newsletter,
            firstname: profile.firstName.trim(),
            lastname: profile.lastName.trim(),
            email: profile.email.trim(),
            acceptMarketing: marketing,
          })),
        },
        publicNewsletterApiContext,
      );
      const results = result.data?.subscribeToNewslettersCustomerIo;
      if (result.error || results?.length !== selected.length || results.some((entry) => entry?.success !== true)) {
        setError('Die Anmeldung ist leider fehlgeschlagen. Bitte versuche es später noch einmal.');
        return;
      }
      setPendingConfirmation(pending);
      setSuccess(true);
      setLoginOpen(false);
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        /* Optional draft storage. */
      }
    } catch {
      setError('Die Anmeldung ist leider fehlgeschlagen. Bitte versuche es später noch einmal.');
    } finally {
      submitting.current = false;
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current || status === 'loading') return;
    if (!selected.length) {
      setError('Bitte wähle mindestens einen Newsletter aus.');
      return;
    }
    if (!event.currentTarget.reportValidity()) return;
    setError(null);
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ selected, marketing, createdAt: Date.now() }));
    } catch {
      /* Optional draft storage. */
    }
    if (status === 'authenticated') {
      await subscribe(session.user);
    } else {
      setStep('start');
      setLoginOpen(true);
    }
  };
  const titles: Partial<Record<LoginStep, string>> = {
    start: 'Deine Newsletter. Dein Account.',
    login: 'Melde dich an.',
    register: 'Erstelle einen kostenlosen Account.',
    registerName: 'Sag uns noch, wie du heißt.',
    unconfirmed: 'Account noch nicht aktiviert.',
    unconfirmed_error: 'Bestätigungsmail konnte nicht gesendet werden.',
    registrationFinish: 'Fast geschafft!',
    nopassword_error: 'Passwort zurücksetzen',
  };
  return (
    <section className="mx-auto w-full max-w-screen-xl px-4 text-foreground">
      <header>
        <Title level={1} classProps={{ heading: 'font-medium text-black dark:text-white' }}>
          {overview?.title || DEFAULT_TITLE}
        </Title>
        <div
          className="prose max-w-none pt-4 text-foreground/70"
          dangerouslySetInnerHTML={{ __html: overview?.subheadline || DEFAULT_SUBHEADLINE }}
        />
      </header>
      {success ? (
        <p role="status" className="pt-12">
          {pendingConfirmation
            ? 'Deine Newsletter-Anmeldung wurde gespeichert. Bitte bestätige deinen Account über die E-Mail in deinem Posteingang.'
            : 'Vielen Dank! Deine Newsletter-Anmeldung war erfolgreich.'}
        </p>
      ) : newsletters.length === 0 ? (
        <p role="status" className="pt-12">
          Die Newsletter-Anmeldung ist derzeit nicht verfügbar. Bitte versuche es später noch einmal.
        </p>
      ) : (
        <form className="pt-8" onSubmit={submit} aria-busy={fetching}>
          <fieldset className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" disabled={fetching || loginOpen}>
            <legend className="sr-only">Newsletter auswählen</legend>
            {newsletters.map((newsletter) => (
              <NewsletterTopicCard
                key={newsletter.id}
                newsletter={newsletter}
                checked={selected.includes(newsletter.id)}
                onChange={() => {
                  setError(null);
                  setSelected((current) =>
                    current.includes(newsletter.id)
                      ? current.filter((id) => id !== newsletter.id)
                      : [...current, newsletter.id],
                  );
                }}
              />
            ))}
          </fieldset>
          {selected.length > 0 && (
            <div className="mt-8 max-w-3xl">
              <NewsletterConsent
                idPrefix="newsletter-overview"
                marketing={marketing}
                onMarketingChange={setMarketing}
                content={consentContent}
              />
              <button
                type="submit"
                disabled={fetching || status === 'loading'}
                className="mt-8 inline-flex min-h-[43px] cursor-pointer items-center justify-center rounded-full border border-primary px-8 py-3 font-herokid text-2xs uppercase tracking-widest transition-colors hover:bg-primary disabled:opacity-60"
              >
                {fetching
                  ? 'Anmeldung wird gesendet …'
                  : status === 'authenticated'
                    ? 'Newsletter abonnieren'
                    : 'Weiter zur Anmeldung'}
              </button>
            </div>
          )}
          {error && !loginOpen && (
            <p role="alert" className="pt-6 text-primary text-sm">
              {error}
            </p>
          )}
        </form>
      )}
      <Dialog title={titles[step] || 'Anmeldung'} isOpen={loginOpen} onClose={() => setLoginOpen(false)}>
        {error && (
          <p role="alert" className="text-primary">
            {error}
          </p>
        )}
        {step === 'start' && (
          <p>Melde dich an oder erstelle einen kostenlosen Account. Deine Newsletter-Auswahl bleibt erhalten.</p>
        )}
        {(step === 'unconfirmed' || step === 'registrationFinish') && (
          <p role="status">
            Deine E-Mail-Adresse wurde noch nicht bestätigt. Bitte überprüfe deinen Posteingang und den Spam-Ordner.
            Deine Newsletter-Auswahl bleibt gespeichert.
          </p>
        )}
        {step === 'unconfirmed_error' && (
          <p role="alert">Bitte versuche es später erneut. Deine Newsletter-Auswahl bleibt gespeichert.</p>
        )}
        {step === 'nopassword_error' && (
          <p role="alert">
            Für diesen Account ist kein gültiges Passwort hinterlegt.{' '}
            <Link href="/auth/password-reset" className="underline">
              Passwort zurücksetzen
            </Link>
            . Deine Newsletter-Auswahl bleibt gespeichert.
          </p>
        )}
        {['unconfirmed', 'unconfirmed_error', 'nopassword_error'].includes(step) && (
          <button type="button" className="mt-4 underline" onClick={() => setStep('start')}>
            Zurück zur Anmeldung
          </button>
        )}
        {step === 'registrationFinish' && registeredProfile && error && (
          <button
            type="button"
            className="mt-4 underline"
            disabled={fetching}
            onClick={() => subscribe(registeredProfile, true)}
          >
            Newsletter-Anmeldung erneut versuchen
          </button>
        )}
        <LoginFlow
          step={step}
          onStepChange={setStep}
          explicitCallbackUrl="/newsletter"
          marketingConsent={marketing}
          classProps={{ root: 'mt-8' }}
          loginSuccessCallback={async () => {
            try {
              const currentSession = await getSession();
              if (!currentSession?.user) {
                setError('Deine Anmeldung konnte nicht übernommen werden. Bitte melde dich erneut an.');
                return;
              }
              await subscribe(currentSession.user);
            } catch {
              setError('Deine Anmeldung konnte nicht übernommen werden. Bitte melde dich erneut an.');
            }
          }}
          registrationSuccessCallback={async (profile) => {
            setRegisteredProfile(profile);
            await subscribe(profile, true);
          }}
        />
      </Dialog>
    </section>
  );
};
